import { makeStyles } from "@material-ui/core/styles";
import { useRef, useState } from "react";
import { changeAppState, getAppState } from "../../../../common/appState";
import { OtherUser, User } from "../../User/types";
import { ConversationArea } from "./components/ConversationArea/ConversationArea";
import { MessageField } from "./components/MessageField";
import { getMessagesPerDays } from "./getMessagesPerDay";
import {
    MessagePanelOpenState,
    MessagesPerDays,
    PresentMessage,
} from "./types";

export function MessageContent({
    targetUser,
    user,
    messages,
    setMessages,
    showMore,
    messagesCountToShow,
    currentlySentMessageNo,
    setCurrentlySentMessageNo,
    panelState,
}: {
    targetUser: OtherUser;
    user: User;
    messages: MessagesPerDays;
    setMessages: (messages: MessagesPerDays) => void;
    showMore: () => Promise<void>;
    messagesCountToShow: number;
    currentlySentMessageNo: number;
    setCurrentlySentMessageNo: (messageId: number) => void;
    panelState: MessagePanelOpenState;
}) {
    const [textareaHeight, setTextareaHeight] = useState(0);
    const unopenedPresents = Object.values(messages)
        .flat()
        .filter(
            m =>
                m.fromUserId === targetUser.userId &&
                m.present &&
                !m.isPresentOpen
        ) as PresentMessage[];
    const [submitting, setSubmitting] = useState(false);

    const c = useMessageContentStyles();

    const [replyTarget, setReplyTarget] = useState("");
    const inputRef = useRef<HTMLInputElement>(null);

    const onlyOwnMessage = user.sul >= 2 || targetUser.sul >= 2;

    return (
        <div className={c.container}>
            <h2 className={c.userName}>{targetUser.name}</h2>

            <ConversationArea
                messages={messages}
                user={user}
                targetUser={targetUser}
                setMessages={setMessages}
                currentlySentMessageNo={currentlySentMessageNo}
                setCurrentlySentMessageNo={setCurrentlySentMessageNo}
                textareaHeight={textareaHeight}
                showMore={showMore}
                messagesCountToShow={messagesCountToShow}
                submitting={submitting}
                setSubmitting={setSubmitting}
                panelState={panelState}
                setReplyTarget={setReplyTarget}
                inputRef={inputRef}
                onlyOwnMessage={onlyOwnMessage}
            />

            <MessageField
                myUserId={user.userId}
                targetUserId={targetUser.userId}
                setMessages={setMessages}
                setCurrentlySentMessageNo={setCurrentlySentMessageNo}
                setTextareaHeight={setTextareaHeight}
                messagesCountToShow={messagesCountToShow}
                showMessageLimitDialog={() =>
                    showMessageLimitDialog(user, targetUser, messages)
                }
                setSendingMessage={(text, isImage) => {
                    setSendingMessage(
                        messages,
                        setMessages,
                        user,
                        text,
                        setCurrentlySentMessageNo,
                        isImage
                    );
                }}
                globalSubmitting={submitting}
                setGlobalSubmitting={setSubmitting}
                user={user}
                isOpenAllButtonShown={
                    unopenedPresents.length >= 2 && !onlyOwnMessage
                }
                replyTarget={replyTarget}
                setReplyTarget={setReplyTarget}
                inputRef={inputRef}
            />
        </div>
    );
}
export const usernameMarginBottom = 8;
export const usernameHeight = 48;
const useMessageContentStyles = makeStyles({
    container: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
    },
    userName: {
        padding: "5px 20px",
        marginBottom: usernameMarginBottom,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        maxWidth: "100%",
        height: usernameHeight,
    },
});

function showMessageLimitDialog(
    user: User,
    targetUser: OtherUser,
    messages: MessagesPerDays
) {
    if (
        getAppState().relatedUsers.some(
            u => u.userId === targetUser.userId && u.isFriend
        )
    ) {
        return false; // Friend is ok
    }
    if (
        !Object.values(messages)
            .flat()
            .some(m => m.fromUserId === user.userId)
    ) {
        return false; // Still not reached the limit
    }
    changeAppState("guestMessageLimitDialogState", {
        type: "open",
        targetUser,
    });
    return true; // Limit reached
}

function setSendingMessage(
    messages: MessagesPerDays,
    setMessages: (messages: MessagesPerDays) => void,
    user: User,
    text: string,
    setCurrentlySentMessageNo: (messageNo: number) => void,
    isImage?: boolean
) {
    const flatMessages = Object.values(messages).flat();
    const messageNo = Math.max(...flatMessages.map(m => m.messageNo)) + 1;
    setMessages(
        getMessagesPerDays([
            ...flatMessages,
            {
                fromUserId: user.userId,
                messageNo,
                text,
                minutesAgo: 0,
                isRead: false,
                isUnsent: false,
                present: "",
                presentQuantity: 0,
                isPresentOpen: false,
                unremovable: false,
                isImage: !!isImage,
                isSending: true,
            },
        ])
    );
    setCurrentlySentMessageNo(messageNo);
}
